import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { AuthState, AuthActions } from '../types/stores';
import type { OrgProfile, UserData } from '../types/api-responses';
// @ts-ignore — JS service files lack type declarations
import { getCurrentUser, loginUser, logoutUser, changePassword, ssoAzureLogin } from '../services/authService';
// @ts-ignore — JS config file lacks type declarations
import { loginRequest } from '../config/msal-config';
// @ts-ignore — JS config file lacks type declarations
import { clearPersistedQueries } from '../config/query-persistence';

const TOKEN_KEY = 'authToken';
const TOKEN_TYPE_KEY = 'tokenType';
const ROLE_KEY = 'ee_assistant_role';
const EMAIL_KEY = 'ee_assistant_email';
const PROFILE_KEY = 'ee_assistant_profile';
const USER_DATA_KEY = 'ee_assistant_user_data';

function _derivePortalTier(role: string | null, orgProfile: OrgProfile | null): string {
  if (role === 'superadmin') return 'superadmin';
  if (role === 'admin') return 'admin';
  if (role === 'leader') return 'leader';
  if (orgProfile?.has_nested_reports === true) return 'leader';
  if (orgProfile?.has_reports === true) return 'leader';
  if (orgProfile?.has_nested_reports == null && orgProfile?.member_role === 'org_leader') return 'leader';
  return 'user';
}

const useAuthStore = create<AuthState & AuthActions>()((set, get) => ({
  isAuthenticated: false,
  userRole: null,
  userEmail: null,
  userProfile: null,
  userData: null,
  authError: '',
  isLoading: true,

  _setAuthState: (token: string, user: UserData, orgProfile: OrgProfile | null) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_TYPE_KEY, 'bearer');
    const role = user.role === 'SuperAdmin' ? 'superadmin'
      : user.is_superuser ? 'admin'
      : (user.role || 'User').toLowerCase();
    const email = user.email || '';
    const previousEmail = localStorage.getItem(EMAIL_KEY);
    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem(EMAIL_KEY, email);
    if (previousEmail && previousEmail !== email) clearPersistedQueries();
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
    const rawProfile = localStorage.getItem(PROFILE_KEY);
    const resolvedProfile: OrgProfile | null = orgProfile || (rawProfile ? JSON.parse(rawProfile) : null);
    if (orgProfile) localStorage.setItem(PROFILE_KEY, JSON.stringify(orgProfile));
    set({ isAuthenticated: true, userRole: role, userEmail: email, userData: user, userProfile: resolvedProfile, authError: '', isLoading: false });
  },

  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TYPE_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    clearPersistedQueries();
    sessionStorage.clear();
    set({ isAuthenticated: false, userRole: null, userEmail: null, userProfile: null, userData: null, isLoading: false });
  },

  bootstrapOptimistic: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { return false; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) { get().clearAuth(); return false; }
    } catch { get().clearAuth(); return false; }

    const cachedUserRaw = localStorage.getItem(USER_DATA_KEY);
    if (!cachedUserRaw) { return false; }
    try {
      const cachedUser: UserData = JSON.parse(cachedUserRaw);
      const rawProfile = localStorage.getItem(PROFILE_KEY);
      const cachedProfile: OrgProfile | null = rawProfile ? JSON.parse(rawProfile) : null;
      get()._setAuthState(token, cachedUser, cachedProfile);
      return true;
    } catch { return false; }
  },

  _skipNextServerValidation: false,

  bootstrapServerValidation: async () => {
    if (get()._skipNextServerValidation) {
      set({ _skipNextServerValidation: false });
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      const user = await getCurrentUser(token);
      const orgProfile: OrgProfile | null = user.org_profile || (localStorage.getItem(PROFILE_KEY) ? JSON.parse(localStorage.getItem(PROFILE_KEY)!) : null);
      get()._setAuthState(token, user, orgProfile);
    } catch { get().clearAuth(); }
  },

  bootstrap: async () => {
    get().bootstrapOptimistic();
    await get().bootstrapServerValidation();
    if (!get().isAuthenticated) set({ isLoading: false });
  },

  login: async (username: string, password: string) => {
    set({ authError: '' });
    try {
      const data = await loginUser(username, password);
      if (data.force_password_change) return { success: false, forcePasswordChange: true, tempToken: data.access_token };
      const token: string = data.access_token;
      const user = await getCurrentUser(token);
      get()._setAuthState(token, user, data.org_profile);
      return { success: true };
    } catch (err: unknown) {
      const e = err as { detail?: string; message?: string };
      const detail = e.detail || e.message || 'Login failed';
      set({ authError: detail });
      return { success: false, error: detail };
    }
  },

  loginWithMicrosoft: async (msalInstance: unknown) => {
    set({ authError: '' });
    try {
      const inst = msalInstance as { loginRedirect: (req: unknown) => Promise<void> };
      await inst.loginRedirect(loginRequest);
      return { success: true };
    } catch (err: unknown) {
      const e = err as { errorCode?: string; name?: string; message?: string; detail?: string };
      if (e.errorCode === 'user_cancelled' || (e.name === 'BrowserAuthError' && e.message?.includes('user_cancelled'))) {
        return { success: false, cancelled: true };
      }
      const detail = e.detail || e.message || 'Microsoft login failed';
      set({ authError: detail });
      return { success: false, error: detail };
    }
  },

  handleRedirectResponse: async (msalInstance: unknown) => {
    try {
      const inst = msalInstance as { handleRedirectPromise: () => Promise<{ idToken: string } | null> };
      const result = await inst.handleRedirectPromise();
      if (!result) return;
      const data = await ssoAzureLogin(result.idToken);
      const user = await getCurrentUser(data.access_token);
      get()._setAuthState(data.access_token, user, data.org_profile);
      set({ _skipNextServerValidation: true });
    } catch (err: unknown) {
      const e = err as { detail?: string; message?: string };
      const detail = e.detail || e.message || 'Microsoft login failed';
      get().clearAuth();
      set({ authError: detail });
    }
  },

  completePasswordChange: async (tempToken: string, newPassword: string) => {
    try {
      const data = await changePassword(tempToken, newPassword);
      const token: string = data.access_token;
      const user = await getCurrentUser(token);
      get()._setAuthState(token, user, null);
      return { success: true };
    } catch (err: unknown) {
      const e = err as { detail?: string; message?: string };
      return { success: false, error: e.detail || e.message || 'Password change failed' };
    }
  },

  logout: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) logoutUser(token);
    get().clearAuth();
  },

  refreshUser: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      const user = await getCurrentUser(token);
      const rawProfile = localStorage.getItem(PROFILE_KEY);
      const orgProfile: OrgProfile | null = user.org_profile || (rawProfile ? JSON.parse(rawProfile) : null);
      get()._setAuthState(token, user, orgProfile);
    } catch { get().clearAuth(); }
  },
}));

export const selectPortalTier = (s: AuthState): string => _derivePortalTier(s.userRole, s.userProfile);
export const selectIsAdmin = (s: AuthState): boolean => s.userRole === 'admin' || s.userRole === 'superadmin';
export const selectIsSuperAdmin = (s: AuthState): boolean => s.userRole === 'superadmin';
export const selectIsLeader = (s: AuthState): boolean => { const t = _derivePortalTier(s.userRole, s.userProfile); return t === 'leader' || t === 'admin' || t === 'superadmin'; };
export const selectIsCreatorOrAbove = (s: AuthState): boolean => s.userRole === 'admin' || s.userRole === 'superadmin' || s.userRole === 'creator';
export const selectHasDirectReports = (s: AuthState): boolean => s.userProfile?.has_reports === true;
export const selectHasAiSpendPilot = (s: AuthState): boolean => s.userData?.ai_spend_pilot === true;

export function useAuth() {
  return useAuthStore(useShallow((s) => {
    const portalTier = _derivePortalTier(s.userRole, s.userProfile);
    return {
      isAuthenticated: s.isAuthenticated,
      userRole: s.userRole,
      userEmail: s.userEmail,
      userProfile: s.userProfile,
      userData: s.userData,
      authError: s.authError,
      isLoading: s.isLoading,
      login: s.login,
      loginWithMicrosoft: s.loginWithMicrosoft,
      handleRedirectResponse: s.handleRedirectResponse,
      completePasswordChange: s.completePasswordChange,
      logout: s.logout,
      refreshUser: s.refreshUser,
      portalTier,
      isSuperAdmin: s.userRole === 'superadmin',
      isAdmin: s.userRole === 'admin' || s.userRole === 'superadmin',
      isLeader: portalTier === 'leader' || portalTier === 'admin' || portalTier === 'superadmin',
      isCreatorOrAbove: s.userRole === 'admin' || s.userRole === 'superadmin' || s.userRole === 'creator',
    };
  }));
}

export default useAuthStore;
